import type { ColorSchemePreference } from '@customerio/pluma-components/react';

const VALID_COLOR_SCHEMES: ColorSchemePreference[] = ['auto', 'light', 'dark'];

export type ThemeReconcileInput = {
  /** Persisted preference from the account bootstrap, or null when unset. */
  backend: ColorSchemePreference | null;
  /** Current locally-cached preference (localStorage), or null when unset. */
  local: ColorSchemePreference | null;
  /** Whether the user has deliberately chosen a scheme on this device. */
  localExplicit: boolean;
  /** Whether the current user has loaded (bootstrap resolved). */
  isAuthenticated: boolean;
};

export type ThemeReconcileResult = {
  /** Scheme to apply + cache locally, or null to leave the local value as-is. */
  next: ColorSchemePreference | null;
  /** Mark the local "explicit preference" flag (the user has a deliberate choice). */
  setExplicit: boolean;
  /** Push the local value up to the backend (it has no stored preference yet). */
  backfill: boolean;
};

function isValidScheme(value: ColorSchemePreference | null): value is ColorSchemePreference {
  return value != null && VALID_COLOR_SCHEMES.includes(value);
}

/**
 * Decides how to reconcile the locally-cached theme with the backend on load.
 *
 * Model: localStorage is the synchronous local cache that drives first paint;
 * the backend is the authoritative cross-device store. The presence of a backend
 * value means the user has an explicit preference; its absence means "no preference,
 * default to auto". The "don't persist defaults" rule lives here on the client —
 * the backend stores whatever valid value it is given, including a deliberate "auto".
 */
export function reconcileTheme(input: ThemeReconcileInput): ThemeReconcileResult {
  const noop: ThemeReconcileResult = { next: null, setExplicit: false, backfill: false };

  // Nothing to reconcile until we know who the user is.
  if (!input.isAuthenticated) {
    return noop;
  }

  // Backend wins as the cross-device source of truth. Always re-assert it (not just when
  // it differs from local): the product-default effect runs in the same commit and may
  // queue a different scheme, so reconcile must set the backend value to override it.
  // Setting the same value React already holds is a no-op (no re-render, no write).
  if (isValidScheme(input.backend)) {
    return { next: input.backend, setExplicit: true, backfill: false };
  }

  // Backend has no stored preference. Backfill a deliberate local choice up (including
  // a deliberate "auto"), but never propagate an untouched default.
  if (input.localExplicit && isValidScheme(input.local)) {
    return { next: null, setExplicit: false, backfill: true };
  }

  return noop;
}
